import { type CountryData, defaultCountries as allCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import { type ChangeEvent, type FocusEvent, type InvalidEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { Select } from '@pkgs/components/components/Select/Select';
import { type CountryCode } from '@pkgs/api';
import { InputText as UiCoreInputText } from '@ev-tech/ui-core-components/InputText';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useInputValidation } from '@/hooks/useInputValidation';

import 'react-international-phone/style.css';

import { getMaxCountryMaskFormat } from './utils';
import { LazyFlagImage } from './LazyFlagImage';
import { type ExposeInputTextProps, type ExposeInputTextSizesProps } from '../TextInput/TextInput.types';
import { type ValidityTypes } from '../TextInput/TextInput';
import { ModalContext } from '../Modal/ModalContext';

import styles from './PhoneInput.module.css';

type PhoneInputProps = Omit<ExposeInputTextProps, 'inputMode' | 'label' | 'size' | 'type'> & {
  label: string;
  name: string;
  initialCountry: CountryCode;
  size?: ExposeInputTextSizesProps;
  isInModal?: boolean;
  validationMessages?: Partial<Record<ValidityTypes, string>>;
  validatesOnBlur?: boolean;
  validatesOnChange?: boolean;
};

const PREFERRED_COUNTRIES = ['de', 'es', 'fr', 'it'] as const;

export const PhoneInputField = ({
  defaultValue,
  initialCountry,
  size = 's',
  isInModal,
  validationMessages,
  validatesOnBlur = true,
  validatesOnChange = true,
  className,
  disabled,
  id,
  isRequired,
  label,
  name,
  onBlur,
  onChange,
  onFocus,
  onInvalid,
  readOnly,
  value,
  ...inputProps
}: PhoneInputProps) => {
  // to avoid having 2 elements on the page with the same id when the contact form modal is open
  const inputTextId = `${id ?? name}${isInModal ? '_modal' : ''}`;
  const { language } = useSettings();
  const [error, setError] = useState('');
  const [displayName, setDisplayName] = useState<Intl.DisplayNames>();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const { isMobile } = useBreakpoint();
  const { isModal } = useContext(ModalContext);

  const updateValidity = useCallback(() => {
    const validity = inputRef.current?.validity;

    if (!validity || validity.valid) {
      setError('');
      return;
    }

    for (const key in validity) {
      if (validity[key as ValidityTypes]) {
        const errorMessage = validationMessages?.[key as ValidityTypes];
        if (!errorMessage) {
          console.warn('No error message provided for:', key);
          continue;
        }
        setError(errorMessage);
        break;
      }
    }
  }, [validationMessages]);

  useEffect(() => {
    if (error) {
      updateValidity();
    }
  }, [error, isRequired, updateValidity]);

  useInputValidation({ inputRef, updateValidity, validationMessages, validatesOnChange });

  useEffect(() => {
    // Node.js and browsers can return different region names. Keep the server
    // and initial client render on the library's static names, then localize.
    setDisplayName(
      new Intl.DisplayNames([language], {
        type: 'region',
      }),
    );
  }, [language]);

  const localizedPreferredCountries = useMemo(() => {
    const preferredFiltered = PREFERRED_COUNTRIES.map((countryCode) => {
      return allCountries.find((country) => country[1] === countryCode);
    }).filter((item): item is CountryData => Boolean(item));

    const localizedPreferredCountries: CountryData[] = preferredFiltered.map((country) => {
      const [, countryCode, ...rest] = country;
      const countryName = displayName?.of(countryCode.toUpperCase()) ?? country[0];
      return [countryName, countryCode, ...rest];
    });
    return localizedPreferredCountries;
  }, [displayName]);

  const { inputValue, handlePhoneValueChange, country, setCountry } = usePhoneInput({
    // Do not set default country on value passed down
    defaultCountry: value ? '' : initialCountry.toLowerCase(),
    inputRef,
    value: value ?? defaultValue,
    countries: allCountries.map((country) => {
      const formattedCountry = [...country] as CountryData;
      formattedCountry[3] = getMaxCountryMaskFormat(parseCountry(country).format, country[2]);
      return formattedCountry;
    }),
    disableFormatting: true,
    forceDialCode: true,
    charAfterDialCode: '',
    disableCountryGuess: false,
    disableDialCodeAndPrefix: true,
  });

  // re-arrange countries list so that preferred countries are always on the top and the current country is always at the very top of the list
  const sortedCountries = useMemo<CountryData[]>(() => {
    const localizedCountries = allCountries.map((country) => {
      const [, countryCode, ...rest] = country;
      const countryName = displayName?.of(countryCode.toUpperCase()) ?? country[0];
      return [countryName, countryCode, ...rest];
    }) as CountryData[];

    if (displayName) {
      localizedCountries.sort((a, b) => a[0].localeCompare(b[0]));
    }

    const nonPreferredCountries = localizedCountries.filter((countryItem) => {
      return !localizedPreferredCountries.some((preferredCountry) => preferredCountry[1] === countryItem[1]);
    });
    const currentPreferredCountryIndex = localizedPreferredCountries.findIndex((item) => item[1] === country.iso2);

    if (currentPreferredCountryIndex !== -1 && localizedPreferredCountries[currentPreferredCountryIndex]) {
      const currentPreferredCountry = localizedPreferredCountries[currentPreferredCountryIndex];

      return [
        currentPreferredCountry,
        ...localizedPreferredCountries.filter((_, index) => index !== currentPreferredCountryIndex),
        ...nonPreferredCountries,
      ];
    }

    const currentCountryIndex = nonPreferredCountries.findIndex((item) => item[1] === country.iso2);

    if (currentCountryIndex !== -1 && nonPreferredCountries[currentCountryIndex]) {
      const currentCountry = nonPreferredCountries[currentCountryIndex];

      return [currentCountry, ...localizedPreferredCountries, ...nonPreferredCountries.filter((_, index) => index !== currentCountryIndex)];
    }

    return [...localizedPreferredCountries, ...nonPreferredCountries];
  }, [localizedPreferredCountries, displayName, country.iso2]);

  const items = useMemo(
    () =>
      sortedCountries.map((item) => {
        const [countryName, countryCode, phoneCode] = item;
        return {
          id: countryCode,
          label: (
            <div className={styles.selectedValue}>
              <LazyFlagImage iso2={countryCode} />
              {`${countryName} (+${phoneCode})`}
            </div>
          ),
          textValue: `${countryName} (+${phoneCode})`,
          description: `${countryName} (+${phoneCode})`,
          selectedLabel: (
            <div className={styles.selectedValue}>
              <FlagImage iso2={countryCode} alt={countryName} />
              {`+${phoneCode}`}
            </div>
          ),
        };
      }),
    [sortedCountries],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);

      // on mobile iOS, wait for the native keyboard to scroll the label out of view
      // and then scroll it back into view
      if (!isMobile || !isModal) return;
      setTimeout(() => {
        labelRef.current?.scrollIntoView({
          behavior: 'instant',
          block: 'start',
        });
      }, 100);
    },
    [isMobile, isModal, onFocus],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);

      if (validatesOnBlur) {
        updateValidity();
      }
    },
    [onBlur, updateValidity, validatesOnBlur],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handlePhoneValueChange(event);
      onChange?.(event);

      if (validatesOnChange) {
        updateValidity();
      }
    },
    [handlePhoneValueChange, onChange, updateValidity, validatesOnChange],
  );

  const handleInvalid = useCallback(
    (event: InvalidEvent<HTMLInputElement>) => {
      onInvalid?.(event);
      updateValidity();
    },
    [onInvalid, updateValidity],
  );

  const inputTextProps: ExposeInputTextProps = {
    ...inputProps,
    error: Boolean(error),
    helperText: error,
    id: inputTextId,
    inputMode: 'tel',
    name,
    onBlur: handleBlur,
    onChange: handleChange,
    onFocus: handleFocus,
    onInvalid: handleInvalid,
    size,
    type: 'text',
    value: inputValue,
    isRequired,
    disabled,
    readOnly,
  };

  return (
    <div className={cn(styles.formGroupWrapper, className)}>
      <label className={styles.label} data-required={isRequired} htmlFor={inputTextId} ref={labelRef}>
        {label}
      </label>
      <div className={styles.phoneInput}>
        <Select
          className={styles.select}
          name={`${name}CountryCode`}
          arrowVariant="triangle"
          items={items}
          label={label}
          hasLabelHidden={true}
          value={country.iso2}
          accessibilityLabel={t('search.select.hiddenAccessibilityText', {
            label: t('search.contact.form.label.phoneAreaCode'),
          })}
          disabled={disabled || readOnly}
          onChange={(key) => {
            setCountry(key as string);
            // focus the input field
            if (inputTextId) {
              document.getElementById(inputTextId)?.focus({ preventScroll: true });
            }
          }}
          size={size}
          isInModal={isInModal}
        />
        <UiCoreInputText {...inputTextProps} ref={inputRef} />
      </div>
    </div>
  );
};
